import type { PriceMap as EnginePriceMap } from "./types";

export type PriceMap = EnginePriceMap;

export type ModelId = "global-index" | "value-tilt" | "multifactor";
export type AiAccountStatus =
  | "DRAFT"
  | "ACTIVE"
  | "PAUSED"
  | "DATA_ERROR"
  | "ARCHIVED";
export type RiskLevel = "CONSERVATIVE" | "BALANCED" | "GROWTH";
export type BaseCurrency = "USD" | "GBP" | "EUR";
export type DataQualityStatus = "HEALTHY" | "WARNING" | "BLOCKING";

export interface InvestmentModelVersion {
  readonly id: string;
  readonly modelId: ModelId;
  readonly name: string;
  readonly version: string;
  readonly description: string;
  readonly targets: Readonly<Record<string, number>>;
  readonly eligibleSymbols: readonly string[];
  readonly benchmarkSymbol: string;
  readonly activatedAt: string;
  readonly retiredAt: string | null;
  readonly configurationChecksum: string;
}

export type AccountLedgerEventType =
  | "OPENING_BALANCE"
  | "DEPOSIT"
  | "WITHDRAWAL"
  | "BUY"
  | "SELL"
  | "DIVIDEND"
  | "FEE"
  | "TAX"
  | "INTEREST"
  | "CORPORATE_ACTION"
  | "ADJUSTMENT";

export interface AccountLedgerEntry {
  readonly id: string;
  readonly accountId: string;
  readonly eventType: AccountLedgerEventType;
  /** Signed cash movement in the account's base currency. */
  readonly amount: number;
  readonly currency: BaseCurrency;
  readonly symbol?: string;
  readonly quantity?: number;
  readonly price?: number;
  readonly orderId?: string;
  readonly fillId?: string;
  readonly eventTime: string;
  readonly description: string;
  readonly operationId: string;
}

export interface PositionLot {
  readonly id: string;
  readonly accountId: string;
  readonly symbol: string;
  readonly purchaseDate: string;
  readonly quantity: number;
  readonly remainingQuantity: number;
  readonly purchasePrice: number;
  readonly fees: number;
  readonly costBasis: number;
  readonly fillId: string;
}

export interface AccountPosition {
  readonly symbol: string;
  readonly quantity: number;
  readonly averageCost: number;
  readonly costBasis: number;
  readonly lastPrice: number;
  readonly marketValue: number;
  readonly unrealizedGainLoss: number;
  readonly lots: readonly PositionLot[];
}

export type DecisionType =
  | "BUY"
  | "SELL"
  | "REBALANCE"
  | "INVEST_CASH"
  | "HOLD"
  | "DATA_HALT";

export type DecisionStatus =
  | "CREATED"
  | "ORDERS_CREATED"
  | "EXECUTED"
  | "PARTIALLY_EXECUTED"
  | "SKIPPED"
  | "FAILED";

export interface DecisionTrade {
  readonly symbol: string;
  readonly side: "BUY" | "SELL";
  readonly quantity: number;
  readonly referencePrice: number;
  readonly estimatedFillPrice: number;
  readonly estimatedValue: number;
  readonly estimatedFee: number;
  readonly currentWeight: number;
  readonly targetWeight: number;
}

export interface AccountTradeDecision {
  readonly id: string;
  readonly accountId: string;
  readonly modelVersionId: string;
  readonly decisionDate: string;
  readonly decisionTime: string;
  readonly dataCutoffTime: string;
  readonly decisionType: DecisionType;
  readonly currentValue: number;
  readonly availableCash: number;
  readonly currentAllocations: Readonly<Record<string, number>>;
  readonly targetAllocations: Readonly<Record<string, number>>;
  readonly proposedTrades: readonly DecisionTrade[];
  readonly estimatedCost: number;
  readonly estimatedTurnover: number;
  readonly status: DecisionStatus;
  readonly explanation: string;
  readonly orderIds: readonly string[];
  readonly operationId: string;
}

export type SimulatedOrderStatus =
  | "CREATED"
  | "SUBMITTED"
  | "PARTIALLY_FILLED"
  | "FILLED"
  | "CANCELLED"
  | "REJECTED"
  | "EXPIRED";

export interface SimulatedOrder {
  readonly id: string;
  readonly accountId: string;
  readonly decisionId: string;
  readonly symbol: string;
  readonly side: "BUY" | "SELL";
  readonly quantity: number;
  readonly orderType: "MARKETABLE_LIMIT";
  readonly limitPrice: number;
  readonly status: SimulatedOrderStatus;
  readonly submittedAt: string;
  readonly filledAt: string | null;
  readonly cancelledAt: string | null;
  readonly operationId: string;
}

export interface SimulatedFill {
  readonly id: string;
  readonly accountId: string;
  readonly orderId: string;
  readonly symbol: string;
  readonly side: "BUY" | "SELL";
  readonly quantity: number;
  readonly referencePrice: number;
  readonly fillPrice: number;
  readonly grossValue: number;
  readonly fee: number;
  readonly filledAt: string;
  readonly priceSource: "NEXT_PERIOD_ESTIMATE";
  readonly operationId: string;
}

export type ActivityCategory =
  | "ACCOUNT"
  | "MODEL_DECISION"
  | "ORDER"
  | "FILL"
  | "DEPOSIT"
  | "WITHDRAWAL"
  | "DIVIDEND"
  | "CORPORATE_ACTION"
  | "ERROR"
  | "SETTINGS_CHANGE";

export interface AccountActivity {
  readonly id: string;
  readonly accountId: string;
  readonly at: string;
  readonly category: ActivityCategory;
  readonly title: string;
  readonly detail: string;
  readonly amount?: number;
  readonly operationId: string;
}

export interface DailyAccountSnapshot {
  readonly id: string;
  readonly accountId: string;
  readonly date: string;
  readonly totalValue: number;
  readonly cash: number;
  readonly investedValue: number;
  readonly dailyReturn: number;
  readonly cumulativeReturn: number;
  readonly benchmarkValue: number;
  readonly benchmarkReturn: number;
  readonly realisedGainLoss: number;
  readonly unrealisedGainLoss: number;
  readonly dividendIncome: number;
  readonly fees: number;
  readonly drawdown: number;
  readonly positionCount: number;
  readonly source: "OFFICIAL" | "SEEDED_DEMO";
}

export interface ExecutionPolicy {
  readonly feeRate: number;
  readonly slippageRate: number;
  readonly minimumCashWeight: number;
  readonly minimumTradeValue: number;
}

export interface AiAccount {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly status: AiAccountStatus;
  readonly baseCurrency: BaseCurrency;
  readonly startingBalance: number;
  readonly currentCash: number;
  readonly currentEquity: number;
  readonly currentTotalValue: number;
  readonly modelId: ModelId;
  readonly modelVersionId: string;
  readonly benchmarkSymbol: string;
  readonly riskLevel: RiskLevel;
  readonly autoInvestingEnabled: boolean;
  readonly allowFractionalShares: boolean;
  readonly executionPolicy: ExecutionPolicy;
  readonly createdAt: string;
  readonly startedAt: string | null;
  readonly pausedAt: string | null;
  readonly lastStrategyRunAt: string | null;
  readonly nextStrategyRunAt: string | null;
  readonly latestDecisionId: string | null;
  readonly positions: readonly AccountPosition[];
  readonly ledger: readonly AccountLedgerEntry[];
  readonly decisions: readonly AccountTradeDecision[];
  readonly orders: readonly SimulatedOrder[];
  readonly fills: readonly SimulatedFill[];
  readonly activity: readonly AccountActivity[];
  readonly snapshots: readonly DailyAccountSnapshot[];
  readonly processedOperationIds: readonly string[];
}

export interface CreateAccountInput {
  readonly id?: string;
  readonly name: string;
  readonly description?: string;
  readonly startingBalance: number;
  readonly baseCurrency?: BaseCurrency;
  readonly modelId: ModelId;
  readonly riskLevel?: RiskLevel;
  readonly benchmarkSymbol?: string;
  readonly autoInvestingEnabled?: boolean;
  readonly allowFractionalShares?: boolean;
  readonly createdAt?: string;
  readonly feeRate?: number;
  readonly slippageRate?: number;
  readonly minimumCashWeight?: number;
  readonly minimumTradeValue?: number;
}

export interface AccountValuedPosition extends AccountPosition {
  readonly currentWeight: number;
  readonly targetWeight: number;
  readonly allocationDrift: number;
}

export interface AccountValuation {
  readonly accountId: string;
  readonly asOf: string;
  readonly cash: number;
  readonly investedValue: number;
  readonly totalValue: number;
  readonly netContributions: number;
  readonly totalReturn: number;
  readonly dailyReturn: number;
  readonly unrealizedGainLoss: number;
  readonly fees: number;
  readonly positions: readonly AccountValuedPosition[];
  readonly dataStatus: DataQualityStatus;
  readonly missingSymbols: readonly string[];
}

export interface DepositAndInvestOptions {
  readonly at?: string;
  /** Reusing a key makes a retried deposit a no-op. */
  readonly operationId?: string;
}

const PRECISION = 1e8;
const DEFAULT_CREATED_AT = "2026-07-18T09:00:00.000Z";

function round(value: number): number {
  return Math.round((value + Number.EPSILON) * PRECISION) / PRECISION;
}

function requirePositive(value: number, label: string): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`${label} must be greater than zero`);
  }
}

function requireRate(value: number, label: string, maximum = 1): void {
  if (!Number.isFinite(value) || value < 0 || value >= maximum) {
    throw new Error(`${label} must be between zero and ${maximum}`);
  }
}

function requireTimestamp(value: string, label: string): void {
  if (!Number.isFinite(Date.parse(value))) throw new Error(`${label} is invalid`);
}

function stableId(value: string): string {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function freezeRecord<T extends object>(value: T): Readonly<T> {
  return Object.freeze(value);
}

function makeModel(
  modelId: ModelId,
  name: string,
  description: string,
  targets: Record<string, number>,
): InvestmentModelVersion {
  const version = "1.0.0";
  const canonicalTargets = Object.entries(targets)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([symbol, weight]) => `${symbol}:${weight}`)
    .join("|");
  return Object.freeze({
    id: `${modelId}-v1`,
    modelId,
    name,
    version,
    description,
    targets: Object.freeze({ ...targets }),
    eligibleSymbols: Object.freeze(Object.keys(targets)),
    benchmarkSymbol: "VT",
    activatedAt: "2026-07-18T00:00:00.000Z",
    retiredAt: null,
    configurationChecksum: stableId(`${modelId}|${version}|${canonicalTargets}`),
  });
}

export const DEFAULT_MODELS: readonly InvestmentModelVersion[] = Object.freeze([
  makeModel(
    "global-index",
    "Global Index",
    "Passive global equity exposure with the lowest model complexity.",
    { VT: 1 },
  ),
  makeModel(
    "value-tilt",
    "Value Tilt",
    "Broad global equity with diversified value and small-cap value tilts.",
    { VT: 0.7, VTV: 0.2, AVUV: 0.1 },
  ),
  makeModel(
    "multifactor",
    "Multifactor",
    "Global core exposure plus value, quality, and momentum satellites.",
    { VT: 0.7, VTV: 0.15, QUAL: 0.1, MTUM: 0.05 },
  ),
]);

function getModel(modelId: ModelId): InvestmentModelVersion {
  const model = DEFAULT_MODELS.find((candidate) => candidate.modelId === modelId);
  if (!model) throw new Error(`Unknown investment model: ${modelId}`);
  return model;
}

function addMinutes(timestamp: string, minutes: number): string {
  return new Date(Date.parse(timestamp) + minutes * 60_000).toISOString();
}

function nextDefaultEventTime(account: AiAccount): string {
  const latest = account.ledger.at(-1)?.eventTime ?? account.createdAt;
  return addMinutes(latest, 1);
}

function nextModelRun(timestamp: string): string {
  const date = new Date(Date.parse(timestamp));
  do {
    date.setUTCDate(date.getUTCDate() + 1);
  } while (date.getUTCDay() === 0 || date.getUTCDay() === 6);
  date.setUTCHours(21, 15, 0, 0);
  return date.toISOString();
}

function freezePosition(position: AccountPosition): AccountPosition {
  return Object.freeze({
    ...position,
    lots: Object.freeze(position.lots.map((lot) => Object.freeze({ ...lot }))),
  });
}

function freezeDecision(decision: AccountTradeDecision): AccountTradeDecision {
  return Object.freeze({
    ...decision,
    currentAllocations: Object.freeze({ ...decision.currentAllocations }),
    targetAllocations: Object.freeze({ ...decision.targetAllocations }),
    proposedTrades: Object.freeze(
      decision.proposedTrades.map((trade) => Object.freeze({ ...trade })),
    ),
    orderIds: Object.freeze([...decision.orderIds]),
  });
}

function freezeAccount(account: AiAccount): AiAccount {
  return Object.freeze({
    ...account,
    executionPolicy: Object.freeze({ ...account.executionPolicy }),
    positions: Object.freeze(account.positions.map(freezePosition)),
    ledger: Object.freeze(account.ledger.map((entry) => Object.freeze({ ...entry }))),
    decisions: Object.freeze(account.decisions.map(freezeDecision)),
    orders: Object.freeze(account.orders.map((order) => Object.freeze({ ...order }))),
    fills: Object.freeze(account.fills.map((fill) => Object.freeze({ ...fill }))),
    activity: Object.freeze(
      account.activity.map((event) => Object.freeze({ ...event })),
    ),
    snapshots: Object.freeze(
      account.snapshots.map((snapshot) => Object.freeze({ ...snapshot })),
    ),
    processedOperationIds: Object.freeze([...account.processedOperationIds]),
  });
}

function totalFees(account: AiAccount): number {
  return round(
    account.ledger
      .filter((entry) => entry.eventType === "FEE")
      .reduce((sum, entry) => sum + Math.abs(entry.amount), 0),
  );
}

function netContributions(account: AiAccount): number {
  return round(
    account.ledger
      .filter(
        (entry) =>
          entry.eventType === "OPENING_BALANCE" ||
          entry.eventType === "DEPOSIT" ||
          entry.eventType === "WITHDRAWAL",
      )
      .reduce((sum, entry) => sum + entry.amount, 0),
  );
}

function refreshPositions(
  positions: readonly AccountPosition[],
  prices: PriceMap,
): { positions: AccountPosition[]; missingSymbols: string[]; investedValue: number } {
  const missingSymbols: string[] = [];
  const refreshed = positions.map((position) => {
    const candidate = prices[position.symbol];
    const validPrice = Number.isFinite(candidate) && candidate > 0;
    if (!validPrice) missingSymbols.push(position.symbol);
    const price = validPrice ? candidate : position.lastPrice;
    const marketValue = round(position.quantity * price);
    return {
      ...position,
      lastPrice: price,
      marketValue,
      unrealizedGainLoss: round(marketValue - position.costBasis),
      lots: [...position.lots],
    };
  });
  return {
    positions: refreshed,
    missingSymbols,
    investedValue: round(
      refreshed.reduce((sum, position) => sum + position.marketValue, 0),
    ),
  };
}

export function valueAccount(
  account: AiAccount,
  prices: PriceMap,
  asOf = nextDefaultEventTime(account),
): AccountValuation {
  requireTimestamp(asOf, "Valuation timestamp");
  const model = getModel(account.modelId);
  const refreshed = refreshPositions(account.positions, prices);
  const totalValue = round(account.currentCash + refreshed.investedValue);
  const contributions = netContributions(account);
  const asOfDate = asOf.slice(0, 10);
  const priorSnapshot = [...account.snapshots]
    .filter((snapshot) => snapshot.date < asOfDate)
    .sort((left, right) => right.date.localeCompare(left.date))[0];
  const todaysFlows = account.ledger
    .filter(
      (entry) =>
        entry.eventTime.slice(0, 10) === asOfDate &&
        (entry.eventType === "DEPOSIT" ||
          entry.eventType === "WITHDRAWAL" ||
          entry.eventType === "OPENING_BALANCE"),
    )
    .reduce((sum, entry) => sum + entry.amount, 0);
  const dailyReturn =
    priorSnapshot && priorSnapshot.totalValue > 0
      ? (totalValue - todaysFlows) / priorSnapshot.totalValue - 1
      : 0;
  const positions = refreshed.positions.map((position) => {
    const currentWeight = totalValue > 0 ? position.marketValue / totalValue : 0;
    const targetWeight = model.targets[position.symbol] ?? 0;
    return Object.freeze({
      ...position,
      lots: Object.freeze([...position.lots]),
      currentWeight,
      targetWeight,
      allocationDrift: currentWeight - targetWeight,
    });
  });
  return Object.freeze({
    accountId: account.id,
    asOf,
    cash: account.currentCash,
    investedValue: refreshed.investedValue,
    totalValue,
    netContributions: contributions,
    totalReturn: contributions > 0 ? (totalValue - contributions) / contributions : 0,
    dailyReturn,
    unrealizedGainLoss: round(
      positions.reduce((sum, position) => sum + position.unrealizedGainLoss, 0),
    ),
    fees: totalFees(account),
    positions: Object.freeze(positions),
    dataStatus:
      refreshed.missingSymbols.length > 0 ? "BLOCKING" : "HEALTHY",
    missingSymbols: Object.freeze([...refreshed.missingSymbols]),
  });
}

function snapshotForAccount(
  account: AiAccount,
  prices: PriceMap,
  at: string,
  source: DailyAccountSnapshot["source"] = "OFFICIAL",
): DailyAccountSnapshot {
  const valuation = valueAccount(account, prices, at);
  const previous = account.snapshots.at(-1);
  const peak = Math.max(
    valuation.totalValue,
    ...account.snapshots.map((snapshot) => snapshot.totalValue),
  );
  return Object.freeze({
    id: `snapshot-${stableId(`${account.id}|${at.slice(0, 10)}|${source}`)}`,
    accountId: account.id,
    date: at.slice(0, 10),
    totalValue: valuation.totalValue,
    cash: valuation.cash,
    investedValue: valuation.investedValue,
    dailyReturn: previous
      ? (valuation.totalValue -
          account.ledger
            .filter(
              (entry) =>
                entry.eventTime.slice(0, 10) === at.slice(0, 10) &&
                (entry.eventType === "DEPOSIT" ||
                  entry.eventType === "WITHDRAWAL"),
            )
            .reduce((sum, entry) => sum + entry.amount, 0)) /
          previous.totalValue -
        1
      : 0,
    cumulativeReturn: valuation.totalReturn,
    benchmarkValue: previous?.benchmarkValue ?? account.startingBalance,
    benchmarkReturn: previous?.benchmarkReturn ?? 0,
    realisedGainLoss: 0,
    unrealisedGainLoss: valuation.unrealizedGainLoss,
    dividendIncome: round(
      account.ledger
        .filter((entry) => entry.eventType === "DIVIDEND")
        .reduce((sum, entry) => sum + entry.amount, 0),
    ),
    fees: valuation.fees,
    drawdown: peak > 0 ? valuation.totalValue / peak - 1 : 0,
    positionCount: valuation.positions.length,
    source,
  });
}

function appendOfficialSnapshot(
  account: AiAccount,
  prices: PriceMap,
  at: string,
): AiAccount {
  const date = at.slice(0, 10);
  if (account.snapshots.some((snapshot) => snapshot.date === date)) {
    const refreshed = refreshPositions(account.positions, prices);
    return freezeAccount({
      ...account,
      positions: refreshed.positions,
      currentEquity: refreshed.investedValue,
      currentTotalValue: round(account.currentCash + refreshed.investedValue),
    });
  }
  const snapshot = snapshotForAccount(account, prices, at);
  return freezeAccount({
    ...account,
    snapshots: [...account.snapshots, snapshot],
    currentEquity: snapshot.investedValue,
    currentTotalValue: snapshot.totalValue,
  });
}

interface InvestContext {
  operationId: string;
  decisionAt: string;
  dataCutoffTime: string;
}

function investAvailableCash(
  source: AiAccount,
  prices: PriceMap,
  context: InvestContext,
): AiAccount {
  const model = getModel(source.modelId);
  const missingSymbols = model.eligibleSymbols.filter(
    (symbol) => !Number.isFinite(prices[symbol]) || prices[symbol] <= 0,
  );
  const refreshed = refreshPositions(source.positions, prices);
  const currentValue = round(source.currentCash + refreshed.investedValue);
  const currentAllocations = Object.freeze(
    Object.fromEntries(
      model.eligibleSymbols.map((symbol) => {
        const position = refreshed.positions.find(
          (candidate) => candidate.symbol === symbol,
        );
        return [
          symbol,
          currentValue > 0 ? (position?.marketValue ?? 0) / currentValue : 0,
        ];
      }),
    ),
  );
  const decisionId = `decision-${stableId(`${source.id}|${context.operationId}|${context.decisionAt}`)}`;

  if (missingSymbols.length > 0) {
    const explanation = `Simulated investing halted because valid prices are missing for ${missingSymbols.join(
      ", ",
    )}.`;
    const decision: AccountTradeDecision = {
      id: decisionId,
      accountId: source.id,
      modelVersionId: model.id,
      decisionDate: context.decisionAt.slice(0, 10),
      decisionTime: context.decisionAt,
      dataCutoffTime: context.dataCutoffTime,
      decisionType: "DATA_HALT",
      currentValue,
      availableCash: source.currentCash,
      currentAllocations,
      targetAllocations: model.targets,
      proposedTrades: [],
      estimatedCost: 0,
      estimatedTurnover: 0,
      status: "FAILED",
      explanation,
      orderIds: [],
      operationId: context.operationId,
    };
    return freezeAccount({
      ...source,
      status: "DATA_ERROR",
      autoInvestingEnabled: false,
      positions: refreshed.positions,
      currentEquity: refreshed.investedValue,
      currentTotalValue: currentValue,
      lastStrategyRunAt: context.decisionAt,
      nextStrategyRunAt: null,
      latestDecisionId: decisionId,
      decisions: [...source.decisions, decision],
      activity: [
        ...source.activity,
        {
          id: `activity-${stableId(`${decisionId}|error`)}`,
          accountId: source.id,
          at: context.decisionAt,
          category: "ERROR",
          title: "Investing paused by data check",
          detail: explanation,
          operationId: context.operationId,
        },
      ],
    });
  }

  const reserve = currentValue * source.executionPolicy.minimumCashWeight;
  const availableCash = Math.max(0, source.currentCash - reserve);
  const deficits = model.eligibleSymbols.map((symbol) => {
    const position = refreshed.positions.find(
      (candidate) => candidate.symbol === symbol,
    );
    const currentMarketValue = position?.marketValue ?? 0;
    const desiredMarketValue =
      currentValue *
      (1 - source.executionPolicy.minimumCashWeight) *
      model.targets[symbol];
    return {
      symbol,
      currentMarketValue,
      deficit: Math.max(0, desiredMarketValue - currentMarketValue),
    };
  });
  const totalDeficit = deficits.reduce((sum, item) => sum + item.deficit, 0);
  const maximumGross =
    availableCash / (1 + source.executionPolicy.feeRate);
  const grossBudget = Math.min(maximumGross, totalDeficit);
  const trades: DecisionTrade[] = [];

  if (grossBudget >= source.executionPolicy.minimumTradeValue) {
    for (const item of deficits) {
      if (item.deficit <= 0 || totalDeficit <= 0) continue;
      const referencePrice = prices[item.symbol];
      const fillPrice = round(
        referencePrice * (1 + source.executionPolicy.slippageRate),
      );
      const allocatedGross = grossBudget * (item.deficit / totalDeficit);
      let quantity = source.allowFractionalShares
        ? Math.floor((allocatedGross / fillPrice) * PRECISION) / PRECISION
        : Math.floor(allocatedGross / fillPrice);
      let estimatedValue = round(quantity * fillPrice);
      let estimatedFee = round(
        estimatedValue * source.executionPolicy.feeRate,
      );
      if (
        estimatedValue + estimatedFee > source.currentCash - reserve &&
        quantity > 0
      ) {
        quantity = source.allowFractionalShares
          ? Math.floor(
              ((source.currentCash - reserve) /
                (fillPrice * (1 + source.executionPolicy.feeRate))) *
                PRECISION,
            ) / PRECISION
          : Math.floor(
              (source.currentCash - reserve) /
                (fillPrice * (1 + source.executionPolicy.feeRate)),
            );
        estimatedValue = round(quantity * fillPrice);
        estimatedFee = round(
          estimatedValue * source.executionPolicy.feeRate,
        );
      }
      if (
        quantity <= 0 ||
        estimatedValue < source.executionPolicy.minimumTradeValue
      ) {
        continue;
      }
      trades.push(
        Object.freeze({
          symbol: item.symbol,
          side: "BUY",
          quantity,
          referencePrice,
          estimatedFillPrice: fillPrice,
          estimatedValue,
          estimatedFee,
          currentWeight:
            currentValue > 0 ? item.currentMarketValue / currentValue : 0,
          targetWeight: model.targets[item.symbol],
        }),
      );
    }
  }

  const estimatedCost = round(
    trades.reduce((sum, trade) => sum + trade.estimatedFee, 0),
  );
  const submittedAt = addMinutes(context.decisionAt, 1);
  const filledAt = addMinutes(context.decisionAt, 2);
  const orderIds = trades.map(
    (trade) =>
      `order-${stableId(`${decisionId}|BUY|${trade.symbol}|${trade.quantity}`)}`,
  );
  const explanation =
    trades.length > 0
      ? `Invested available virtual cash into ${trades
          .map((trade) => trade.symbol)
          .join(", ")} in proportion to allocation shortfalls; no positions were sold.`
      : `No simulated order cleared the ${source.executionPolicy.minimumTradeValue.toFixed(
          2,
        )} minimum after retaining the cash reserve.`;
  const decision: AccountTradeDecision = {
    id: decisionId,
    accountId: source.id,
    modelVersionId: model.id,
    decisionDate: context.decisionAt.slice(0, 10),
    decisionTime: context.decisionAt,
    dataCutoffTime: context.dataCutoffTime,
    decisionType: trades.length > 0 ? "INVEST_CASH" : "HOLD",
    currentValue,
    availableCash: source.currentCash,
    currentAllocations,
    targetAllocations: model.targets,
    proposedTrades: trades,
    estimatedCost,
    estimatedTurnover: 0,
    status: trades.length > 0 ? "EXECUTED" : "SKIPPED",
    explanation,
    orderIds,
    operationId: context.operationId,
  };

  let cash = source.currentCash;
  const positions = refreshed.positions.map((position) => ({
    ...position,
    lots: [...position.lots],
  }));
  const ledger = [...source.ledger];
  const orders = [...source.orders];
  const fills = [...source.fills];
  const activity = [
    ...source.activity,
    {
      id: `activity-${stableId(`${decisionId}|decision`)}`,
      accountId: source.id,
      at: context.decisionAt,
      category: "MODEL_DECISION" as const,
      title: trades.length > 0 ? "Investment decision ready" : "No trade today",
      detail: explanation,
      operationId: context.operationId,
    },
  ];

  trades.forEach((trade, index) => {
    const orderId = orderIds[index];
    const fillId = `fill-${stableId(`${orderId}|${filledAt}`)}`;
    const requiredCash = round(trade.estimatedValue + trade.estimatedFee);
    if (requiredCash > cash + 0.000001) return;
    cash = round(cash - requiredCash);
    const order: SimulatedOrder = {
      id: orderId,
      accountId: source.id,
      decisionId,
      symbol: trade.symbol,
      side: "BUY",
      quantity: trade.quantity,
      orderType: "MARKETABLE_LIMIT",
      limitPrice: trade.estimatedFillPrice,
      status: "FILLED",
      submittedAt,
      filledAt,
      cancelledAt: null,
      operationId: context.operationId,
    };
    const fill: SimulatedFill = {
      id: fillId,
      accountId: source.id,
      orderId,
      symbol: trade.symbol,
      side: "BUY",
      quantity: trade.quantity,
      referencePrice: trade.referencePrice,
      fillPrice: trade.estimatedFillPrice,
      grossValue: trade.estimatedValue,
      fee: trade.estimatedFee,
      filledAt,
      priceSource: "NEXT_PERIOD_ESTIMATE",
      operationId: context.operationId,
    };
    orders.push(order);
    fills.push(fill);
    ledger.push(
      {
        id: `ledger-${stableId(`${fillId}|buy`)}`,
        accountId: source.id,
        eventType: "BUY",
        amount: -trade.estimatedValue,
        currency: source.baseCurrency,
        symbol: trade.symbol,
        quantity: trade.quantity,
        price: trade.estimatedFillPrice,
        orderId,
        fillId,
        eventTime: filledAt,
        description: `Simulated purchase of ${trade.quantity} ${trade.symbol}`,
        operationId: context.operationId,
      },
      {
        id: `ledger-${stableId(`${fillId}|fee`)}`,
        accountId: source.id,
        eventType: "FEE",
        amount: -trade.estimatedFee,
        currency: source.baseCurrency,
        symbol: trade.symbol,
        orderId,
        fillId,
        eventTime: filledAt,
        description: `Simulated trading fee for ${trade.symbol}`,
        operationId: context.operationId,
      },
    );
    const lot: PositionLot = {
      id: `lot-${stableId(fillId)}`,
      accountId: source.id,
      symbol: trade.symbol,
      purchaseDate: filledAt,
      quantity: trade.quantity,
      remainingQuantity: trade.quantity,
      purchasePrice: trade.estimatedFillPrice,
      fees: trade.estimatedFee,
      costBasis: requiredCash,
      fillId,
    };
    const existingIndex = positions.findIndex(
      (position) => position.symbol === trade.symbol,
    );
    const previous =
      existingIndex >= 0
        ? positions[existingIndex]
        : {
            symbol: trade.symbol,
            quantity: 0,
            averageCost: 0,
            costBasis: 0,
            lastPrice: trade.referencePrice,
            marketValue: 0,
            unrealizedGainLoss: 0,
            lots: [] as PositionLot[],
          };
    const quantity = round(previous.quantity + trade.quantity);
    const costBasis = round(previous.costBasis + requiredCash);
    const marketValue = round(quantity * trade.referencePrice);
    const nextPosition = {
      ...previous,
      quantity,
      averageCost: round(costBasis / quantity),
      costBasis,
      lastPrice: trade.referencePrice,
      marketValue,
      unrealizedGainLoss: round(marketValue - costBasis),
      lots: [...previous.lots, lot],
    };
    if (existingIndex >= 0) positions[existingIndex] = nextPosition;
    else positions.push(nextPosition);
    activity.push(
      {
        id: `activity-${stableId(`${orderId}|submitted`)}`,
        accountId: source.id,
        at: submittedAt,
        category: "ORDER",
        title: `Simulated order submitted for ${trade.symbol}`,
        detail: `${trade.quantity} shares · marketable limit`,
        amount: -trade.estimatedValue,
        operationId: context.operationId,
      },
      {
        id: `activity-${stableId(`${fillId}|filled`)}`,
        accountId: source.id,
        at: filledAt,
        category: "FILL",
        title: `Bought ${trade.quantity} shares of ${trade.symbol}`,
        detail: `Simulated fill at ${trade.estimatedFillPrice.toFixed(2)} plus ${trade.estimatedFee.toFixed(2)} fee`,
        amount: -requiredCash,
        operationId: context.operationId,
      },
    );
  });

  const finalPositions = refreshPositions(positions, prices);
  const result = freezeAccount({
    ...source,
    currentCash: cash,
    currentEquity: finalPositions.investedValue,
    currentTotalValue: round(cash + finalPositions.investedValue),
    positions: finalPositions.positions,
    ledger,
    decisions: [...source.decisions, decision],
    orders,
    fills,
    activity,
    lastStrategyRunAt: context.decisionAt,
    nextStrategyRunAt:
      source.status === "ACTIVE" ? nextModelRun(context.decisionAt) : null,
    latestDecisionId: decisionId,
  });
  return appendOfficialSnapshot(result, prices, filledAt);
}

export function createAiAccount(
  input: CreateAccountInput,
  prices: PriceMap,
): AiAccount {
  if (!input.name.trim()) throw new Error("Account name is required");
  requirePositive(input.startingBalance, "Starting balance");
  const createdAt = input.createdAt ?? DEFAULT_CREATED_AT;
  requireTimestamp(createdAt, "Account creation timestamp");
  const feeRate = input.feeRate ?? 0.0005;
  const slippageRate = input.slippageRate ?? 0.0005;
  const minimumCashWeight = input.minimumCashWeight ?? 0.01;
  const minimumTradeValue = input.minimumTradeValue ?? 25;
  requireRate(feeRate, "Fee rate", 0.1);
  requireRate(slippageRate, "Slippage rate", 0.1);
  requireRate(minimumCashWeight, "Minimum cash weight", 0.5);
  requirePositive(minimumTradeValue, "Minimum trade value");
  const model = getModel(input.modelId);
  const autoInvestingEnabled = input.autoInvestingEnabled ?? true;
  const accountId =
    input.id ??
    `account-${stableId(
      `${input.name.trim()}|${createdAt}|${input.modelId}|${input.startingBalance}`,
    )}`;
  const operationId = `create-${stableId(accountId)}`;
  const baseCurrency = input.baseCurrency ?? "USD";
  const openingEntry: AccountLedgerEntry = {
    id: `ledger-${stableId(`${accountId}|opening`)}`,
    accountId,
    eventType: "OPENING_BALANCE",
    amount: round(input.startingBalance),
    currency: baseCurrency,
    eventTime: createdAt,
    description: "Opening virtual cash balance",
    operationId,
  };
  const creationActivity: AccountActivity = {
    id: `activity-${stableId(`${accountId}|created`)}`,
    accountId,
    at: createdAt,
    category: "ACCOUNT",
    title: `${input.name.trim()} created`,
    detail: `${model.name} ${model.version} assigned with a simulated starting balance.`,
    amount: input.startingBalance,
    operationId,
  };
  let account = freezeAccount({
    id: accountId,
    name: input.name.trim(),
    description: input.description?.trim() ?? "",
    status: autoInvestingEnabled ? "ACTIVE" : "DRAFT",
    baseCurrency,
    startingBalance: round(input.startingBalance),
    currentCash: round(input.startingBalance),
    currentEquity: 0,
    currentTotalValue: round(input.startingBalance),
    modelId: input.modelId,
    modelVersionId: model.id,
    benchmarkSymbol: input.benchmarkSymbol ?? model.benchmarkSymbol,
    riskLevel: input.riskLevel ?? "GROWTH",
    autoInvestingEnabled,
    allowFractionalShares: input.allowFractionalShares ?? true,
    executionPolicy: {
      feeRate,
      slippageRate,
      minimumCashWeight,
      minimumTradeValue,
    },
    createdAt,
    startedAt: autoInvestingEnabled ? createdAt : null,
    pausedAt: null,
    lastStrategyRunAt: null,
    nextStrategyRunAt: autoInvestingEnabled ? nextModelRun(createdAt) : null,
    latestDecisionId: null,
    positions: [],
    ledger: [openingEntry],
    decisions: [],
    orders: [],
    fills: [],
    activity: [creationActivity],
    snapshots: [],
    processedOperationIds: [operationId],
  });

  if (autoInvestingEnabled) {
    account = investAvailableCash(account, prices, {
      operationId,
      decisionAt: addMinutes(createdAt, 1),
      dataCutoffTime: createdAt,
    });
  } else {
    account = appendOfficialSnapshot(account, prices, createdAt);
  }
  return account;
}

export function setAccountStatus(
  account: AiAccount,
  status: AiAccountStatus,
  at = nextDefaultEventTime(account),
  operationId = `status-${stableId(`${account.id}|${status}|${at}`)}`,
): AiAccount {
  requireTimestamp(at, "Status timestamp");
  if (account.processedOperationIds.includes(operationId)) return account;
  if (account.status === "ARCHIVED" && status !== "ARCHIVED") {
    throw new Error("An archived account cannot be reactivated");
  }
  if (account.status === status) return account;
  const active = status === "ACTIVE";
  const detail =
    status === "ACTIVE"
      ? "Automatic simulated investing resumed."
      : status === "PAUSED"
        ? "New simulated trades are paused; valuations may continue."
        : status === "ARCHIVED"
          ? "The account was archived and can no longer trade."
          : `Account status changed to ${status}.`;
  return freezeAccount({
    ...account,
    status,
    autoInvestingEnabled: active,
    startedAt: active ? account.startedAt ?? at : account.startedAt,
    pausedAt: status === "PAUSED" ? at : active ? null : account.pausedAt,
    nextStrategyRunAt: active ? nextModelRun(at) : null,
    processedOperationIds: [...account.processedOperationIds, operationId],
    activity: [
      ...account.activity,
      {
        id: `activity-${stableId(operationId)}`,
        accountId: account.id,
        at,
        category: "SETTINGS_CHANGE",
        title: `Account ${status.toLowerCase()}`,
        detail,
        operationId,
      },
    ],
  });
}

export function depositAndInvest(
  account: AiAccount,
  amount: number,
  prices: PriceMap,
  options: DepositAndInvestOptions = {},
): AiAccount {
  requirePositive(amount, "Deposit amount");
  if (account.status === "ARCHIVED") {
    throw new Error("Cannot deposit into an archived account");
  }
  const at = options.at ?? nextDefaultEventTime(account);
  requireTimestamp(at, "Deposit timestamp");
  const operationId =
    options.operationId ??
    `deposit-${stableId(`${account.id}|${at}|${round(amount)}`)}`;
  if (account.processedOperationIds.includes(operationId)) return account;
  const deposited = freezeAccount({
    ...account,
    currentCash: round(account.currentCash + amount),
    currentTotalValue: round(account.currentTotalValue + amount),
    ledger: [
      ...account.ledger,
      {
        id: `ledger-${stableId(`${operationId}|deposit`)}`,
        accountId: account.id,
        eventType: "DEPOSIT",
        amount: round(amount),
        currency: account.baseCurrency,
        eventTime: at,
        description: "Virtual cash contribution",
        operationId,
      },
    ],
    activity: [
      ...account.activity,
      {
        id: `activity-${stableId(`${operationId}|deposit`)}`,
        accountId: account.id,
        at,
        category: "DEPOSIT",
        title: "Virtual cash added",
        detail: "The contribution was recorded before any allocation decision.",
        amount: round(amount),
        operationId,
      },
    ],
    processedOperationIds: [...account.processedOperationIds, operationId],
  });
  if (
    deposited.status !== "ACTIVE" ||
    !deposited.autoInvestingEnabled
  ) {
    return appendOfficialSnapshot(deposited, prices, at);
  }
  return investAvailableCash(deposited, prices, {
    operationId,
    decisionAt: addMinutes(at, 1),
    dataCutoffTime: at,
  });
}

function createSeededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  };
}

function seedSnapshots(account: AiAccount, days = 45): AiAccount {
  const random = createSeededRandom(Number.parseInt(stableId(account.id), 16));
  const returns: number[] = [];
  let rawValue = account.startingBalance;
  for (let day = 0; day < days; day += 1) {
    const dailyReturn = 0.00022 + (random() - 0.5) * 0.018;
    rawValue *= 1 + dailyReturn;
    returns.push(rawValue);
  }
  const scale = account.currentTotalValue / returns.at(-1)!;
  const values = returns.map((value) => value * scale);
  let benchmark = account.startingBalance;
  let peak = 0;
  const dates: string[] = [];
  let date = new Date("2026-05-18T21:15:00.000Z");
  while (dates.length < days) {
    if (date.getUTCDay() !== 0 && date.getUTCDay() !== 6) {
      dates.push(date.toISOString().slice(0, 10));
    }
    date.setUTCDate(date.getUTCDate() + 1);
  }
  const snapshots = values.map((totalValue, index) => {
    const previousValue = index > 0 ? values[index - 1] : account.startingBalance;
    benchmark *= 1 + 0.0002 + (random() - 0.5) * 0.015;
    peak = Math.max(peak, totalValue);
    return Object.freeze({
      id: `snapshot-${stableId(`${account.id}|${dates[index]}|seed`)}`,
      accountId: account.id,
      date: dates[index],
      totalValue: round(totalValue),
      cash: account.currentCash,
      investedValue: round(Math.max(0, totalValue - account.currentCash)),
      dailyReturn: index > 0 ? totalValue / previousValue - 1 : 0,
      cumulativeReturn:
        (totalValue - account.startingBalance) / account.startingBalance,
      benchmarkValue: round(benchmark),
      benchmarkReturn:
        (benchmark - account.startingBalance) / account.startingBalance,
      realisedGainLoss: 0,
      unrealisedGainLoss: round(totalValue - account.startingBalance),
      dividendIncome: 0,
      fees: totalFees(account),
      drawdown: peak > 0 ? totalValue / peak - 1 : 0,
      positionCount: account.positions.length,
      source: "SEEDED_DEMO" as const,
    });
  });
  return freezeAccount({ ...account, snapshots });
}

/** Returns three reproducible demo accounts; all snapshot history is labelled SEEDED_DEMO. */
export function seedAccounts(prices: PriceMap): AiAccount[] {
  const specs: Array<
    Pick<CreateAccountInput, "id" | "name" | "modelId" | "startingBalance">
  > = [
    {
      id: "ai-global-index",
      name: "Global Index AI",
      modelId: "global-index",
      startingBalance: 100_000,
    },
    {
      id: "ai-value-tilt",
      name: "Value Tilt AI",
      modelId: "value-tilt",
      startingBalance: 100_000,
    },
    {
      id: "ai-multifactor",
      name: "Multifactor AI",
      modelId: "multifactor",
      startingBalance: 100_000,
    },
  ];
  return specs.map((spec, index) =>
    seedSnapshots(
      createAiAccount(
        {
          ...spec,
          createdAt: `2026-05-${String(18 + index).padStart(2, "0")}T20:00:00.000Z`,
          baseCurrency: "USD",
          riskLevel: "GROWTH",
          autoInvestingEnabled: true,
          allowFractionalShares: true,
        },
        prices,
      ),
    ),
  );
}
